import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@/lib/routes';
import { useT } from '@/lib/i18n';
import type { CleI18n } from '@i18n';
import { useAuth } from '@/hooks/useAuth';
import { usePendingBrandsCount } from '@/modules/admin/validation/hooks/usePendingBrandsCount';
import { usePendingSkusCount } from '@/modules/admin/validation/hooks/usePendingSkusCount';
import { usePendingPdvsCount } from '@/modules/admin/validation/hooks/usePendingPdvsCount';
import { usePendingMerchsCount } from '@/modules/admin/validation/hooks/usePendingMerchsCount';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { LucideIcon } from 'lucide-react';
import {
  ChevronDown, LayoutGrid,
  Bell, MapPin, Tag, Building2, Users, Navigation,
  Monitor, FileText, History,
  Settings, // `#1068` `Network` retiré (Market Cluster masqué)
  SlidersHorizontal, // `#1091` — Segmentation rétablie
  ClipboardCheck, UserCheck, GitMerge,
} from 'lucide-react';

/**
 * MODULES — dropdown de navigation entre modules (HOTFIX-NAV-REORG).
 *
 * Règle : tout module accessible via un widget WidgetSidebar (W1-W9) est EXCLU.
 * Exclus (widget) : /marche (W1), /performance (W2), /data-quality (W4),
 *                   /flashinfo/feed (W5), /geo-data (W6), /comparator (W7),
 *                   /lab (W8), /admin/alerting (W9).
 *
 * Supprimés : "Flash Info — Créer" (entrée menu retirée), "Imports" (standalone).
 * "Historique imports" redistribué sous Administration.
 * 5 groupes : Tableau de bord · Référentiels · Administration · Configuration · Validation.
 */
interface ModuleItem {
  /**
   * ⚠ `#847` — **`label` DEVIENT OPTIONNEL, ET C'EST LE POINT.** *Il ne survit
   *   que pour les entrées SANS clé — les invariants `#800` (`PDV`, `SKU`,
   *   `Routings`, `Audit Logs`, `Market Clusters`). Partout ailleurs il a été
   *   RETIRÉ : **un repli français derrière une clé, c'est du français qui
   *   dort**, et c'est exactement ce que le sixième trou cachait (`#844`).*
   */
  label?: string;
  /** `#796` i18n — clé catalogue si le terme est validé au glossaire ; sinon
   *   repli sur `label` littéral (items back-office traduits au P3). */
  labelKey?: CleI18n;
  path: string;
  permission: string | string[];
  icon: LucideIcon;
}
interface ModuleGroup {
  /** ⚠ `#847` — optionnel, comme celui des items : un repli derrière une clé dort. */
  label?: string;
  labelKey?: CleI18n;
  items: ModuleItem[];
}

const MODULES: ModuleGroup[] = [
  {
    labelKey: 'nav.tableauDeBord',
    items: [
      /**
       * ⚠⚠ `#847` ★ — **TRANCHÉ : L'ÉCRAN DIT « ACCUEIL ».** *Cette entrée était
       *   laissée littérale au T1 parce que le catalogue disait `Accueil` et
       *   l'écran `Welcome` — un désaccord, pas une décision. Hkm arbitre le
       *   08/09 : c'est le catalogue qui a raison. **`nav.welcome` existait
       *   déjà** (`Accueil` / `Home`) : on réutilise, on ne recrée pas (`#819`).*
       */
      { labelKey: 'nav.welcome', icon: Bell, path: ROUTES.welcome, permission: 'analyse.voir' },
    ],
  },
  {
    labelKey: 'nav.referentiels',
    items: [
      { label: 'PDV',           icon: MapPin,      path: ROUTES.referentielsPdv,           permission: 'referentiels.voir' },
      { label: 'SKU',           icon: Tag,         path: ROUTES.referentielsSku,           permission: 'referentiels.voir' },
      { labelKey: 'commun.marques', icon: Building2,   path: ROUTES.referentielsBrands,        permission: 'analyse.voir' },
      { label: 'Merchandisers', icon: Users,       path: ROUTES.referentielsMerchandisers, permission: 'performance.voir' },
      // ⚠ `#800` — `Routings` : FR == EN, invariant (décision Hkm 08/09).
      { label: 'Routings',      icon: Navigation,  path: ROUTES.referentielsRoutings,      permission: ['referentiels.voir', 'performance.voir'] },
    ],
  },
  {
    labelKey: 'nav.administration',
    items: [
      { labelKey: 'nav.monitoring',        icon: Monitor,  path: ROUTES.adminMonitoring,    permission: 'supervision.voir' },
      /**
       * ⚠⚠ `#847` — *`Audit Logs` n'était PAS un invariant : le T1 ★1 l'avait
       *    tranché, et le `<h1>` de la page dit `Journaux d'audit` depuis le T4
       *    ★8. **Le menu et la page se contredisaient**, et le terme était
       *    classé invariant — donc invisible au scan pour toujours.*
       */
      { labelKey: 'nav.auditLogs',   icon: FileText, path: ROUTES.adminAuditLogs,     permission: 'supervision.voir' },
      { labelKey: 'nav.historiqueImports', icon: History,  path: ROUTES.adminImportHistory, permission: 'imports.voir' },
    ],
  },
  {
    labelKey: 'nav.configuration',
    items: [
      // `#1091` — SEGMENTATION RÉTABLIE (masquée à tort au `#1072`).
      { labelKey: 'nav.segmentation',   icon: SlidersHorizontal, path: ROUTES.adminSegmentation,   permission: 'segmentation.voir' },
      // ⚠ `#1068` — MARKET CLUSTER MASQUÉ (faute de besoin, décision Hkm). Le
      //   code reste (page + modals + route), un ticket de suppression suivra ;
      //   ici l'entrée de menu est retirée. Cf. `#847` pour le classement d'origine.
      // { labelKey: 'nav.marketClusters', icon: Network,     path: ROUTES.adminMarketClusters, permission: 'referentiels.voir' },
      { labelKey: 'nav.parametres',     icon: Settings,          path: ROUTES.parametres,          permission: ['configuration.modifier', 'configuration.modifier', 'utilisateurs.voir'] },
    ],
  },
  {
    labelKey: 'nav.validation',
    items: [
      /**
       * ⚠ `#847` ★ — *`Issues` disparaît, comme dans le `<h1>` de la page au
       *   `#845` ★2 : **la nav et la page disent enfin le même mot.***
       */
      { labelKey: 'ref.rfidRawdata', icon: GitMerge, path: ROUTES.adminValidationOrphans, permission: 'imports.gerer' },
      /**
       * ⚠⚠ `#847` — **LES QUATRE CLÉS EXISTAIENT DEPUIS LE T1, ET PERSONNE NE
       *    LES APPELAIT ICI.** *`routes.ts` les utilise pour le fil d'Ariane
       *    depuis le `#839` ; ce sélecteur, lui, peignait ses propres chaînes.
       *    **La même route portait donc DEUX noms** — `SKU validation` dans le
       *    fil, `Validation SKUs` dans le menu. Le sixième trou (`#844`) cachait
       *    ce côté-ci : les libellés vivent dans une TABLE, pas dans du JSX.*
       * ⚠ *★ `Validation Brands` devient `Validation marques` — dernier `brand`
       *   de l'écran français, après ceux du `#845` ★1.*
       */
      // #1076 P2 — VOIR les pages de validation = `validation.voir` (action 40 :
      // SU AO CJ AN MO). Les ACTIONS de validation à l'intérieur restent gardées par
      // leurs permissions (sku.edit, pdv.edit, brand.edit, merch.identity_view — 41-44).
      { labelKey: 'nav.validationSkus',    icon: Tag,           path: ROUTES.adminValidationSkus,    permission: 'validation.voir' },
      { labelKey: 'nav.validationPdvs',    icon: ClipboardCheck,path: ROUTES.adminValidationPdvs,    permission: 'validation.voir' },
      { labelKey: 'nav.validationMerchs',  icon: UserCheck,     path: ROUTES.adminValidationMerch,   permission: 'validation.voir' },
      { labelKey: 'nav.validationBrands',  icon: Building2,     path: ROUTES.adminValidationBrands,  permission: 'validation.voir' },
    ],
  },
];

/** Label du module actif déduit du pathname (traduit si clé catalogue). */
function useActiveModuleLabel(): string {
  const { pathname } = useLocation();
  const t = useT();
  for (const group of MODULES) {
    for (const item of group.items) {
      if (pathname.startsWith(item.path)) return item.labelKey ? t(item.labelKey) : (item.label ?? '');
    }
  }
  return t('nav.modules');
}

/**
 * ModuleSelector — dropdown de navigation entre modules.
 * Chaque item est conditionné par HasPermission.
 */
export function ModuleSelector() {
  const navigate = useNavigate();
  const activeLabel = useActiveModuleLabel();
  const t = useT();
  const { hasPermission } = useAuth();
  // Badges pending en attente (silencieux si 0 ou erreur réseau)
  const { data: pendingBrandsCountData } = usePendingBrandsCount();
  const pendingBrandsCount = pendingBrandsCountData?.count ?? 0;
  const { data: pendingSkusCountData }   = usePendingSkusCount();
  const pendingSkusCount   = pendingSkusCountData?.count   ?? 0;
  const { data: pendingPdvsCountData }   = usePendingPdvsCount();
  const pendingPdvsCount   = pendingPdvsCountData?.count   ?? 0;
  const { data: pendingMerchsCountData } = usePendingMerchsCount();
  const pendingMerchsCount = pendingMerchsCountData?.count ?? 0;

  // Filtrer les items et les groupes vides selon les permissions de l'utilisateur.
  // Evite d'afficher un header de groupe sans aucun item visible.
  const visibleGroups = MODULES
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        hasPermission(item.permission as string | string[])
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('commun.selectionnerModule')}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-danger/50 bg-accent-danger text-white hover:bg-accent-danger/90 active:bg-accent-danger/80"
        >
          <LayoutGrid size={13} className="shrink-0" aria-hidden="true" />
          {activeLabel}
          <ChevronDown size={12} className="shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-52">
        {visibleGroups.map((group, gi) => (
          <DropdownMenuGroup key={group.labelKey ?? group.label}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.07em] text-muted-foreground/80">
              {group.labelKey ? t(group.labelKey) : (group.label ?? '')}
            </DropdownMenuLabel>
            {group.items.map((item) => {
              const Icon = item.icon;
              const pendingCount =
                item.path === ROUTES.adminValidationBrands ? pendingBrandsCount
                : item.path === ROUTES.adminValidationSkus  ? pendingSkusCount
                : item.path === ROUTES.adminValidationPdvs  ? pendingPdvsCount
                : item.path === ROUTES.adminValidationMerch ? pendingMerchsCount
                : 0;
              const showBrandBadge = pendingCount > 0;
              return (
                <DropdownMenuItem
                  key={item.path}
                  onSelect={() => navigate(item.path)}
                  className="cursor-pointer pl-4 text-[10px] gap-1.5 focus:bg-brand-samsung-500/15 focus:text-brand-samsung-400 data-[highlighted]:bg-brand-samsung-500/15 data-[highlighted]:text-brand-samsung-400"
                >
                  <Icon size={10} className="shrink-0 opacity-60" aria-hidden="true" />
                  <span className="flex-1">{item.labelKey ? t(item.labelKey) : (item.label ?? '')}</span>
                  {showBrandBadge && (
                    <span className="ml-auto rounded-full bg-accent-warning/20 px-1.5 py-0 text-[9px] font-semibold text-accent-warning leading-4">
                      {pendingCount}
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
